import { useCallback } from "react";

import { DateTime } from "luxon";

import {
  generateRecurrenceRuleString,
  FREQUENCIES,
} from "../../../../Recurrence";
import { useObjectMemo } from "../../../hooks";
import {
  useRequestWithFeedback,
  combineRunProps,
  type OverrideRunProps,
  useIdempotencyErrorHandling,
  parseErrorJson,
  parseErrorResponse,
  type ErrorResponse,
  type AnyException,
  type Data,
} from "../../../composites";
import { useLibrary } from "../../../providers";
import { useAppDispatch } from "../utils";
import { useACHCompany } from "../achCompanies";
import { useInstitution } from "../institution";

import { API } from "./api";
import { actions } from "./slice";
import { BulkPaymentForm } from "./BulkPayment.form";
import { getScreenWithError } from "./helpers";
import { resolveExpediteChoice } from "./expedite";

export const useFetchPaginatedBulkPayments = () => {
  const { send, loading, status } = useRequestWithFeedback<
    { results: API.BulkPayment[]; page: { totalElements: number } },
    unknown
  >();

  const dispatch = useAppDispatch();

  const fetchPage = useCallback(
    (
      pageNumber: number,
      offsetIndex: number,
      pageSize: number,
      queryString?: string,
      ordering?: string,
      disableCaching?: boolean,
    ) => {
      send({
        action: API.fetchBulkPayments(
          offsetIndex,
          pageSize,
          queryString,
          ordering,
          disableCaching,
        ),
        onData: ({ results, page: { totalElements } }) => {
          dispatch(
            actions.upsertPage({
              pageNumber,
              results,
              total: totalElements,
            }),
          );
        },
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchPage,
    loading,
  });
};

export const useFetchBulkPayments = () => {
  const { send, loading, status } = useRequestWithFeedback<
    { results: API.BulkPayment[]; page: { totalElements: number } },
    unknown
  >();

  const dispatch = useAppDispatch();

  const fetchNext = useCallback(
    (
      offsetIndex: number,
      pageSize: number,
      queryString?: string,
      ordering?: string,
      disableCaching?: boolean,
    ) => {
      send({
        action: API.fetchBulkPayments(
          offsetIndex,
          pageSize,
          queryString,
          ordering,
          disableCaching,
        ),
        onData: ({ results, page: { totalElements } }) => {
          if (offsetIndex === 0) {
            dispatch(actions.replaceResults({ results, total: totalElements }));
          } else {
            dispatch(
              actions.appendResults({
                results,
                total: totalElements,
              }),
            );
          }
        },
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchNext,
    loading,
  });
};

export const useFetchBulkPaymentById = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.BulkPayment,
    unknown
  >();

  const dispatch = useAppDispatch();

  const fetchBulkPaymentById = useCallback(
    (uuid: API.BulkPaymentId | API.BulkReversalId) => {
      return new Promise<API.BulkPayment>((resolve, reject) => {
        send({
          action: API.fetchBulkPaymentById(uuid),
          onData: (data) => {
            dispatch(actions.updateSelected(data));
            resolve(data);
            return data;
          },
          onError: (err) => {
            reject(err);
          },
        });
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchBulkPaymentById,
    loading,
  });
};

export const useGetBulkReversal = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.BulkPayment,
    unknown
  >();

  const getBulkReversal = useCallback(
    (uuid: API.BulkReversalId) => {
      return send({
        action: API.fetchBulkPaymentById(uuid),
      });
    },
    [send],
  );

  return useObjectMemo({
    error: status === "error",
    send: getBulkReversal,
    loading,
  });
};

export const useGetBulkReversalsForBulkPayment = () => {
  const { send, loading, status } = useRequestWithFeedback<
    { results: API.BulkReversal[]; page: { totalElements: number } },
    unknown
  >();

  const getBulkReversals = useCallback(
    (bulkPaymentId: API.BulkPaymentId) => {
      return send({
        action: API.fetchBulkReversalsForBulkPayment(bulkPaymentId),
      });
    },
    [send],
  );

  return useObjectMemo({
    error: status === "error",
    send: getBulkReversals,
    loading,
  });
};

export const useCreateBulkPayment = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.CreateBulkPayment.Response,
    API.CreateBulkPayment.Error
  >();
  const { values } = BulkPaymentForm.useForm();
  const achCompany = useACHCompany();
  const institution = useInstitution();
  const { expedited_payments: expeditedPaymentsFeature } =
    institution?.features ?? {};
  const canHandleIdempotencyErrors = useIdempotencyErrorHandling(
    "payment-idempotency-error",
  );

  const createBulkPayment = useCallback(
    (
      idempotencyKey: UUID,
      overrideRunProps: {
        onSuccess?: () => void;
        onError?: (error: any) => void;
      } = {},
      deviceId?: string,
      deviceRequestId?: string,
      organizationFileUuid?: string,
    ) => {
      const {
        title,
        fromAccount,
        secCode,
        description,
        entryDesc,
        groupPosting,
        payments,
        chosenExpediteOption,
      } = values;

      const formattedPayments = payments.map((payment) => {
        const {
          scheduledDate,
          recipient,
          description: paymentDescription,
          amount,
          speedStatus,
          transactionType,
        } = payment;

        const formattedDate = DateTime.fromFormat(
          scheduledDate,
          "M/d/yyyy",
        ).toFormat("yyyy-MM-dd");

        const rrule = generateRecurrenceRuleString(
          FREQUENCIES.ONCE,
          formattedDate,
          null,
          null,
        );

        return {
          recipient: recipient?.id,
          description: paymentDescription,
          amount,
          ...(rrule && { recurring_rule: rrule }),
          // omitted entirely when the user has not opted into expediting
          ...(expeditedPaymentsFeature &&
            chosenExpediteOption &&
            speedStatus && {
              expedite_choice: resolveExpediteChoice({
                chosenExpediteOption,
                speedStatus,
              }),
            }),
          transaction_type: transactionType === "collect" ? "debit" : "credit",
        };
      });

      const formattedValues = {
        title,
        institution_account: fromAccount as API.AccountId,
        sec_code: secCode,
        description,
        entry_desc: entryDesc,
        group_posting: groupPosting,
        payments: formattedPayments,
        company: achCompany?.id,
        device_id: deviceId,
        device_request_id: deviceRequestId,
        ...(organizationFileUuid && {
          organization_file_uuid: organizationFileUuid,
        }),
      };

      send({
        action: API.createBulkPayment(idempotencyKey, formattedValues),
        onSuccess: overrideRunProps.onSuccess,
        onError: async (error) => {
          const json = await error?.response?.json().catch(() => undefined);

          const { errors } = parseErrorJson(json);
          if (canHandleIdempotencyErrors(errors)) return;

          const screenWithError = getScreenWithError(json);
          overrideRunProps.onError?.(screenWithError);
        },
      });
    },
    [
      send,
      values,
      achCompany,
      expeditedPaymentsFeature,
      canHandleIdempotencyErrors,
    ],
  );

  return useObjectMemo({
    error: status === "error",
    createBulkPayment,
    loading,
  });
};

export const useDeleteBulkPayment = () => {
  const { send, loading } = useRequestWithFeedback();
  const l10n = useLibrary("translations");
  const { throwToast } = useLibrary("toasts");

  const deleteBulkPayment = useCallback(
    (uuid: API.BulkPaymentId, overrideRunProps: OverrideRunProps = {}) => {
      const runProps = combineRunProps(
        {
          onSuccess: async () => {
            throwToast({
              kind: "success",
              message: l10n.getString(
                "delete-payment-success-message",
                null,
                "Payment canceled.",
              ),
            });
          },
          onError: async (error: ErrorResponse<AnyException<Data>>) => {
            const { errors } = await parseErrorResponse(error);
            const defaultError = l10n.getString(
              "delete-payment-error-message",
              null,
              "We could not cancel your payment. Please try again.",
            );
            throwToast({
              kind: "error",
              message: errors.length
                ? `${errors[0].description}`
                : defaultError,
            });
          },
        },
        overrideRunProps,
      );

      try {
        send({
          action: API.deleteBulkPayment(uuid),
          ...runProps,
        });
      } catch (error) {
        runProps.onError?.(
          error as ErrorResponse<AnyException<Data>>,
          () => null,
        );
        runProps.onFinally?.();
      }
    },
    [send, l10n, throwToast],
  );

  return useObjectMemo({
    deleteBulkPayment,
    loading,
  });
};

export const useApproveBulkPayment = () => {
  const { send, loading, status } = useRequestWithFeedback<
    any, // TODO: replace with API.ApproveBulkPayment.Response
    any // TODO: replace with API.ApproveBulkPayment.Error
  >();

  const approveBulkPayment = useCallback(
    (uuid: API.BulkPaymentId) => {
      return send({
        action: API.approveBulkPayment(uuid),
        onData: (data) => {
          return data;
        },
      });
    },
    [send],
  );

  return useObjectMemo({
    error: status === "error",
    send: approveBulkPayment,
    loading,
  });
};

export const useCancelBulkPayment = () => {
  const { send, loading, status } = useRequestWithFeedback<
    any, // TODO: replace with API.CancelBulkPayment.Response
    any // TODO: replace with API.CancelBulkPayment.Error
  >();

  const cancelBulkPayment = useCallback(
    (uuid: API.BulkPaymentId) => {
      return send({
        action: API.cancelBulkPayment(uuid),
        onData: (data) => {
          return data;
        },
      });
    },
    [send],
  );

  return useObjectMemo({
    error: status === "error",
    send: cancelBulkPayment,
    loading,
  });
};

export const useCreateBulkReversal = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.CreateBulkReversal.Response,
    API.CreateBulkReversal.Error
  >();
  const l10n = useLibrary("translations");
  const { throwToast } = useLibrary("toasts");

  const createBulkReversal = useCallback(
    (
      {
        bulkPaymentId,
        reversalReason,
        transactionIds,
        memo,
      }: {
        bulkPaymentId: API.BulkPaymentId;
        reversalReason: API.CreateBulkReversal.Request["reversal_reason"];
        transactionIds?: API.ACHPaymentId[];
        memo?: string;
      },
      overrideRunProps: OverrideRunProps<
        API.CreateBulkReversal.Response,
        AnyException<Data>
      > = {},
    ) => {
      const payload: API.CreateBulkReversal.Request = {
        bulk_payment: bulkPaymentId,
        reversal_reason: reversalReason,
        // omit / pass undefined to reverse every eligible processed child
        // pass an explicit list to reverse only that subset
        ...(transactionIds !== undefined && {
          unverified_ach_transactions: transactionIds,
        }),
        ...(memo !== undefined && { memo }),
      };

      return send({
        action: API.createBulkReversal(payload),
        ...combineRunProps(
          {
            onData: (data) => data,
            onError: async (error: ErrorResponse<AnyException<Data>>) => {
              const { errors } = await parseErrorResponse(error);
              const defaultError = l10n.getString(
                "bulk-reversal-error-message",
                null,
                "We could not reverse these payments. Please try again.",
              );
              throwToast({
                kind: "error",
                message: errors.length
                  ? `${errors[0].description}`
                  : defaultError,
              });
            },
          },
          overrideRunProps,
        ),
      });
    },
    [send, l10n, throwToast],
  );

  return useObjectMemo({
    error: status === "error",
    send: createBulkReversal,
    loading,
  });
};
